import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Card, 
  Title,
  Text,
  TextInput
} from '@tremor/react';
import EnhancedTable from '../components/common/EnhancedTable';
import '../components/common/EnhancedTable/EnhancedTableFix.css';
import { supabase } from '../lib/supabase';
import { useMarketReviewFilters } from '../hooks/useMarketReviewFilters';
import MarketReviewFilters from '../components/MarketReviews/MarketReviewFilters';
import ActiveMarketReviewFilters from '../components/MarketReviews/ActiveMarketReviewFilters';
import ExpandableText from '../components/common/ExpandableText';
import { formatTextWithLinks } from '../utils/formatters';
import { Search, Filter } from 'lucide-react';
import type { MarketReview, MarketReviewFilter } from '../types/market-review';

type SortField = 'review_lot' | 'review_date' | 'review_ucv' | 'review_next';

export default function MarketReviewsPage() {
  // Sorting state
  const [sortField, setSortField] = useState<SortField>('review_next');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<MarketReviewFilter>({
    lot: null,
    reviewType: null,
    reviewDateAfter: null,
    reviewDateBefore: null,
    nextReviewBefore: null
  });

  // Fetch review types to get the descriptions
  const { data: reviewTypes } = useQuery({
    queryKey: ['review_types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('market_review_types')
        .select('review_type, description');
        
      if (error) {
        console.error('Error fetching review types:', error);
        throw error;
      }
      
      return data || [];
    }
  });
  
  // Create a mapping of review type codes to descriptions
  const reviewTypeMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (reviewTypes) {
      reviewTypes.forEach(type => {
        map[type.review_type] = type.description;
      });
    }
    return map;
  }, [reviewTypes]);

  const { data: reviews = [], isLoading, error } = useQuery<MarketReview[]>({
    queryKey: ['market-reviews'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('market_reviews')
          .select('*');

        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }

        return data || [];
      } catch (error) {
        console.error('Error fetching market reviews:', error);
        throw error;
      }
    }
  });

  // Apply filters
  const filteredReviews = useMarketReviewFilters(reviews, searchQuery, filters);

  // Extract unique values for filter dropdowns
  const uniqueLots = React.useMemo(() => 
    Array.from(new Set(reviews.map(review => review.review_lot))).sort(),
    [reviews]
  );

  const uniqueReviewTypes = React.useMemo(() => 
    Array.from(new Set(reviews.map(review => review.review_type))).sort(),
    [reviews]
  );

  // Sort the filtered data
  const sortedReviews = React.useMemo(() => {
    return [...filteredReviews].sort((a, b) => {
      if (sortDirection === 'desc') [a, b] = [b, a];
      switch (sortField) {
        case 'review_lot': return (a.review_lot || '').localeCompare(b.review_lot || '');
        case 'review_date': return (a.review_date || '').localeCompare(b.review_date || '');
        case 'review_ucv': return (a.review_ucv || 0) - (b.review_ucv || 0);
        case 'review_next': return (a.review_next || '').localeCompare(b.review_next || '');
        default: return 0;
      }
    });
  }, [filteredReviews, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    setSortDirection(prev => sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc');
    setSortField(field);
  };

  const getSortIcon = (field: SortField) => {
    if (field !== sortField) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const handleRemoveFilter = (key: keyof MarketReviewFilter) => {
    setFilters(prev => ({ ...prev, [key]: null }));
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatTableDate = (dateString: string) => {
    if (!dateString) return '-';
    const [year, month, day] = dateString.split('-');
    if (!year || !month || !day) return dateString;
    return `${day}/${month}/${year}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          <h3 className="font-semibold">Error loading market reviews</h3>
          <p className="text-sm mt-1">
            {error instanceof Error ? error.message : 'An unknown error occurred'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 h-[calc(100vh-6rem)] overflow-y-auto">
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Market Reviews</h1>
          <p className="text-sm text-gray-600">Property market value reviews and upcoming review dates</p>
        </div>
        <ActiveMarketReviewFilters 
          filters={filters} 
          onRemoveFilter={handleRemoveFilter} 
          reviewTypeMap={reviewTypeMap}
        />
      </div>

      <div className="flex items-center gap-2 mb-4">
        <div className="relative w-full sm:w-64">
          <TextInput
            placeholder="Search market reviews..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>
        <button
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
        >
          <Filter className="h-4 w-4" />
          <span>Filter</span>
        </button>
        <div className="text-base font-bold text-gray-700">
          Total Reviews: {filteredReviews.length}
        </div>
      </div>

      <div className="p-4 border-b border-gray-200">
        <MarketReviewFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          isFilterOpen={isFilterOpen}
          onFilterToggle={() => setIsFilterOpen(!isFilterOpen)}
          filters={filters}
          onFilterChange={(newFilters) => setFilters(prev => ({ ...prev, ...newFilters }))}
          reviewTypes={uniqueReviewTypes}
          lots={uniqueLots}
          reviewTypeMap={reviewTypeMap}
        />
      </div>
      
      <div className="mt-4">
        <EnhancedTable
          columns={[
            { 
              id: 'review_lot', 
              header: (
                <button onClick={() => handleSort('review_lot')} className="flex items-center gap-1">
                  Lot {getSortIcon('review_lot')}
                </button>
              ),
              accessor: (row) => (
                <div className="truncate" title={row.review_lot || 'Not specified'}>
                  {formatTextWithLinks(row.review_lot || 'Not specified')}
                </div>
              ),
              width: 'narrow'
            },
            { 
              id: 'review_date', 
              header: (
                <button onClick={() => handleSort('review_date')} className="flex items-center gap-1">
                  Review Date {getSortIcon('review_date')}
                </button>
              ),
              accessor: (row) => {
                const formattedDate = formatTableDate(row.review_date);
                return (
                  <div className="truncate" title={formattedDate}>
                    {formattedDate}
                  </div>
                );
              },
              width: 'narrow'
            },
            { 
              id: 'review_type', 
              header: 'Type',
              accessor: (row) => {
                const typeText = reviewTypeMap[row.review_type] || row.review_type;
                return (
                  <div className="truncate" title={typeText || 'Not specified'}>
                    {formatTextWithLinks(typeText || 'Not specified')}
                  </div>
                );
              },
              width: 'narrow'
            },
            { 
              id: 'review_ucv', 
              header: (
                <button onClick={() => handleSort('review_ucv')} className="flex items-center gap-1">
                  UCV {getSortIcon('review_ucv')}
                </button>
              ),
              accessor: (row) => {
                const formattedCurrency = formatCurrency(row.review_ucv);
                return (
                  <div className="truncate text-right" title={formattedCurrency}>
                    {formattedCurrency}
                  </div>
                );
              },
              width: 'narrow',
              align: 'right'
            },
            { 
              id: 'review_next', 
              header: (
                <button onClick={() => handleSort('review_next')} className="flex items-center gap-1">
                  Next Review {getSortIcon('review_next')}
                </button>
              ),
              accessor: (row) => {
                const formattedDate = formatTableDate(row.review_next);
                return (
                  <div className="truncate" title={formattedDate}>
                    {formattedDate}
                  </div>
                );
              },
              width: 'narrow'
            },
            { 
              id: 'review_comment', 
              header: 'Notes and Docs',
              accessor: (row) => (
                <ExpandableText 
                  text={row.review_comment || 'Not specified'} 
                  maxLength={40}
                  className="text-sm"
                />
              ),
              width: 'wide'
            }
          ]}
          data={sortedReviews}
          keyField="review_lot"
          width="2800px"
          maxHeight="calc(100vh - 16rem)"
          emptyMessage="No market reviews found"
          horizontalScroll={true}
        />
      </div>
    </div>
  );
}
